import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment, SegmentId } from '@hotjar/webapp/features/Recordings/types';
import type { SiteId } from '@hotjar/site';

import type { AxiosError } from 'axios';
import * as SegmentApi from '@hotjar/webapp/domains/segments/api';
import type { FormMode } from './FormModeOption';

export interface TemporarySegment extends Omit<Segment, 'id' | 'name'> {
  id: SegmentId | null;
  name: string | null;
}

interface Props {
  formMode: FormMode;
  segment: TemporarySegment;
  segmentName: string;
  currentSiteId: SiteId;
  saveForwardingSettings: (segmentId?: SegmentId) => void;
  onSegmentChange: (segment: Segment) => void;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string, error: AxiosError) => boolean;
}

export function useSave({
  formMode,
  segment,
  segmentName,
  currentSiteId,
  saveForwardingSettings,
  onSegmentChange,
  onClose,
  onSuccess,
  onError,
}: Props): [() => Promise<void>] {
  const { t } = useTranslation();
  const save = useCallback(async () => {
    switch (formMode) {
      case 'update': {
        const segmentId = segment.id;

        if (segmentId === null) {
          return;
        }

        const updatedSegment = {
          ...segment,
          id: segmentId,
          name: segmentName,
        };

        SegmentApi.update(currentSiteId, segmentId, updatedSegment);
        saveForwardingSettings();

        onSegmentChange(updatedSegment);
        onClose();

        break;
      }
      case 'create': {
        const newSegment = {
          ...segment,
          name: segmentName,
        };
        try {
          const {
            data: { segment_id: newSegmentId },
          } = await SegmentApi.create(currentSiteId, newSegment);

          saveForwardingSettings(newSegmentId);

          if (newSegmentId !== null) {
            onSegmentChange({ ...newSegment, id: newSegmentId });
            onSuccess(
              t('common:recordings.segment-edit-create.save-success', {
                segmentName,
              }),
            );
          }

          onClose();
        } catch (err) {
          // @ts-expect-error fix catch error type
          const handled: boolean = onError(err.message, err);

          if (!handled) {
            onClose();
          }
        }
        break;
      }
      default: {
        throw new Error('Unknown form mode');
      }
    }
  }, [
    formMode,
    segment,
    segmentName,
    currentSiteId,
    saveForwardingSettings,
    onSegmentChange,
    t,
    onClose,
    onError,
    onSuccess,
  ]);

  return [save];
}
